import {
  ChangeDetectorRef,
  Directive,
  ElementRef,
  EventEmitter,
  HostBinding,
  InjectionToken,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as R from 'ramda';
import { Observable, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';

import { KeyDownListenerService, OutsideClickListenerService } from '../../providers';
import { DomHandler } from '../../services';
import { hasNoValue, hasValue, isEvent, isNotEmpty, isOneOf, isString, KeyCode, toString } from '../../util';
import { BaseInputDirective } from '../base-input.directive';
import { InputSize } from '../input';
import { SelectActions } from './select-actions';
import { SelectItemComponent } from './select-item.component';
import { SelectMenuComponent } from './select-menu.component';

type RemoteCall = (searchTerm, ...params) => Observable<any>;
type PropertyGetter = (option: any) => string;
type PropertyPath = string;
type DisplayProperty = PropertyPath | PropertyGetter;
type DisplayPropertyGetter = (option: any) => string;

export const WithSelectToken = new InjectionToken<BaseSelectDirective>('WithSelectToken');

const resolveDisplayPropertyGetter = (displayProperty: DisplayProperty): DisplayPropertyGetter => {
  if (hasNoValue(displayProperty)) {
    return R.identity;
  }

  return isString(displayProperty)
    ? R.path(R.split('.', displayProperty))
    : displayProperty;
};

@Directive()
export abstract class BaseSelectDirective extends BaseInputDirective implements OnInit, OnDestroy, SelectActions {
  @Input() labelDetails: string;
  @Input() options = [];
  @Input() fetchOptions: RemoteCall;
  @Input() fetchOptionsParams = [];
  @Input() displayProperty: DisplayProperty;
  @Input() displayAdditionalProperties: string[];
  @Input() filterProperties: string[] = [];
  @Input() size: InputSize = InputSize.Default;
  @Input()
  @HostBinding('class.row') @HostBinding('class.g-0') horizontal = true;
  @Input()
  @HostBinding('class.tooltip-for-value') tooltipForValue = false;
  @Input() labelSize = 6;

  @Output() selectionChanged = new EventEmitter();

  @ViewChild(SelectMenuComponent, { read: ElementRef, static: false }) selectMenuRef: ElementRef;
  @ViewChildren(SelectItemComponent, { read: ElementRef }) selectItemsRef: QueryList<ElementRef>;

  public resolveDisplayProperty: DisplayPropertyGetter;
  public input = new Subject<string>();
  public input$: Observable<string>;
  public filteredOptions = [];
  public focused = false;
  public focusedItemIndex = -1;
  public startItemIndex = 0;
  public showFirstOption = false;
  public searchTerm = '';
  public showSelectionMenu = false;
  public loading = false;
  public abstract selected: any;

  private readonly debounceTime = 400;
  private inputSubscription;
  private eventHandlers: any = [
    [isEvent(KeyCode.Tab), () => this.hide()],
    [isEvent(KeyCode.Down), () => this.next()],
    [isEvent(KeyCode.Up), () => this.previous()],
    [isEvent(KeyCode.Enter), () => this.selectActive()],
    [isEvent(KeyCode.Esc), () => this.hide()]
  ];
  private translate = (message: string): string =>
    message && this.translateService.instant(message);

  constructor(
    public translateService: TranslateService,
    public keyDownListener: KeyDownListenerService,
    public outsideClickListener: OutsideClickListenerService,
    public domHandler: DomHandler,
    public cd: ChangeDetectorRef
  ) {
    super();
  }

  abstract select(option: any): void;
  abstract onFocus(): void;
  abstract isSelected(option: any): boolean;
  abstract selectFirstOption(): void;
  abstract isFirstSelected(): boolean;
  onSelected(): void {
  }

  ngOnInit(): void {
    this.resolveDisplayProperty = resolveDisplayPropertyGetter(this.displayProperty);
    this.input$ = this.input.asObservable();
    this.startItemIndex = this.showFirstOption ? 1 : 0;

    if (this.isRemoteSearch()) {
      this.input$ = this.input$.pipe(
        debounceTime(this.debounceTime),
        filter(hasValue)
      );
    }

    this.input$ = this.input$.pipe(
      distinctUntilChanged(),
      switchMap((value: string) => this.filter(toString(value)))
    );
  }

  ngOnDestroy(): void {
    this.unbindAll();
  }

  defaultPlaceholder(): string {
    return hasValue(this.placeholder)
      ? this.placeholder
      : this.remoteSearch()
        ? 'placeholder.startTyping'
        : '';
  }

  updateFilter(): void {
    this.input.next(this.searchTerm);
  }

  selectOption(option): void {
    this.select(option);
    this.onSelected();
    this.propagateChange(this.selected);
    this.propagateTouched(this.selected);
    this.selectionChanged.emit(this.selected);
  }

  getOptionValue(option): string {
    if (option && this.resolveDisplayProperty) {
      return this.translate(toString(this.resolveDisplayProperty(option)));
    }

    return option ? this.translate(toString(option)) : '';
  }

  getOptionValueWithAdditionalProperties(option): string {
    return this.displayAdditionalProperties ?
      this.withAdditionalProperties(option)
      : this.getOptionValue(option);
  }

  withAdditionalProperties(option) {
    return R.join('; ', [
      this.getOptionValue(option),
      ...this.getPropertiesValues(this.displayAdditionalProperties, option, 'n/a')
    ]);
  }

  getPropertiesValues(properties, option, defaultValue) {
    const toProperty: any = R.map((property: any) => R.path(R.split('.', property), option));
    const toValue: any = R.map((property: string) => hasValue(property) ? this.translate(toString(property)) : defaultValue);
    const getValues: any = R.compose(toValue, toProperty);

    return getValues(properties);
  }

  selectActive(): void {
    if (this.focusedItemIndex === 0 && this.hasAdditionalOption()) {
      this.selectFirstOption();
    } else {
      this.selectOption(this.getFocusedOption());
    }
  }

  getFocusedOption(): any {
    return this.filteredOptions[this.focusedItemIndex - this.startItemIndex];
  }

  resetFocus(): void {
    this.focusedItemIndex = -1;
  }

  isLoading(): boolean {
    return this.isRemoteSearch() && this.loading;
  }

  isRemoteSearch(): boolean {
    return !R.isNil(this.fetchOptions);
  }

  isActive(index = -1): boolean {
    return this.focusedItemIndex === index + this.startItemIndex;
  }

  shouldShowSelectionMenu(): boolean {
    return this.showSelectionMenu && !this.isLoading() && isNotEmpty(this.filteredOptions);
  }

  hasAdditionalOption(): boolean {
    return this.startItemIndex > 0;
  }

  // Input Events
  onChange(event: any): void {
    this.searchTerm = event.target.value;
    this.show();
    this.updateFilter();
    this.resetFocus();
  }

  onBlur(event): void {
    this.propagateTouched(event);
  }

  onKeyPress(): void {
    this.show();
  }

  onKeyDown(event): void {
    if (isEvent(KeyCode.Down, event) && !this.showSelectionMenu) {
      this.show();
    }
  }
  // Input Events

  show(): void {
    if (!this.showSelectionMenu && !this.readonly) {
      this.inputSubscription = this.input$
        .subscribe((value: any) => {
          this.loading = false;
          this.filteredOptions = value;
        });
      this.updateFilter();
      this.showSelectionMenu = true;
      this.outsideClickListener.bindDocumentClickListener(this.hide.bind(this));
      this.keyDownListener.bindDocumentKeyDownListener(this.handleKeyDown.bind(this));
    }
  }

  hide(): void {
    this.unbindAll();
    this.resetFocus();
    this.focused = false;
    this.filteredOptions = [];
    this.loading = false;
    this.showSelectionMenu = false;
    this.searchTerm = '';
  }

  remoteSearch(): boolean {
    return hasValue(this.fetchOptions);
  }

  private filter(value: string): Observable<any> {
    return this.fetchOptions ?
      this.fetch() : of(this.options).pipe(
        map(options =>
          R.filter(option =>
            this.containsValue(value, option),
          options
          )
        )
      );
  }

  private fetch(): Observable<any> {
    this.loading = true;
    this.cd.markForCheck();
    return this.fetchOptions(this.searchTerm, ...this.fetchOptionsParams).pipe(tap(() => this.cd.markForCheck()));
  }

  private containsValue(value: string, option: any): boolean {
    const isValueContainedIn = (optionValue) => R.includes(value.toLowerCase(), optionValue.toLowerCase());

    return R.any(propertyValue => isValueContainedIn(propertyValue))(this.getPropertiesValues(this.filterProperties, option, '')) || isValueContainedIn(this.getOptionValue(option));
  }

  private unbindAll() {
    this.unsubscribe();
    this.outsideClickListener.unbindDocumentClickListener();
    this.keyDownListener.unbindDocumentKeyDownListener();
  }

  private unsubscribe() {
    if (this.inputSubscription) {
      this.inputSubscription.unsubscribe();
    }
  }

  protected handleKeyDown(event: KeyboardEvent): void {
    if (this.showSelectionMenu) {
      if (this.shouldPrevent(event)) {
        event.preventDefault();
      }
      const executeHandler: any = R.cond(this.eventHandlers);
      executeHandler(event);
    }
  }

  private shouldPrevent(event): boolean {
    return isOneOf([
      KeyCode.Down,
      KeyCode.Up,
      KeyCode.Enter,
      KeyCode.Esc
    ], event);
  }

  // Scroll
  private next(): void {
    this.focusedItemIndex = R.inc(R.min(this.focusedItemIndex, this.filteredOptions.length - 1));
    this.scroll();
  }

  private previous(): void {
    this.focusedItemIndex = R.dec(R.max(this.focusedItemIndex, 1));
    this.scroll();
  }

  private scroll(): void {
    const selectedElement = R.nth(this.focusedItemIndex, this.selectItemsRef.toArray());

    if (selectedElement) {
      this.domHandler.scrollToElement(this.selectMenuRef.nativeElement, selectedElement.nativeElement);
    }
  }

}
